import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { MapPin, Mail, Clock } from 'lucide-react';

export default function ContactPage() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
    // Honeypot: real users never fill this in. Bots fill every field they see.
    website: ''
  });
  const [mountedAt] = useState(() => Date.now());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, _t: Date.now() - mountedAt })
      });
      if (res.ok) {
        toast({ title: "Message sent!", description: "We'll get back to you within 24 hours." });
        setFormData({ firstName: '', lastName: '', email: '', subject: '', message: '', website: '' });
      } else {
        throw new Error('Failed to send');
      }
    } catch {
      toast({ title: "Something went wrong", description: "Please try again or email us at hello@edufocalgroup.com.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-card">
      <Header />

      {/* Hero */}
      <div className="bg-primary text-white py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-white/70 mb-3">Get In Touch</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">We'd Love to Hear From You</h1>
          <p className="text-lg text-white/80 max-w-xl mx-auto">
            Whether you have a question, need support, or want to make a payment, we're here to help.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto max-w-6xl px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-16">

          {/* Left: Form */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Send Us a Message</h2>
            <p className="text-muted-foreground text-sm mb-8">Prefer email? Fill out the form and we'll get back to you within 24 hours.</p>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Honeypot: hidden from real users, attractive to bots */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                <label htmlFor="website">Leave this field empty</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-foreground font-medium text-sm">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="rounded-xl border-border focus:border-primary focus:ring-primary"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-foreground font-medium text-sm">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="rounded-xl border-border focus:border-primary focus:ring-primary"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground font-medium text-sm">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="rounded-xl border-border focus:border-primary focus:ring-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject" className="text-foreground font-medium text-sm">Subject</Label>
                <Select value={formData.subject} onValueChange={(val) => setFormData(prev => ({ ...prev, subject: val }))}>
                  <SelectTrigger className="rounded-xl border-border">
                    <SelectValue placeholder="What can we help with?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                    <SelectItem value="Subscription & Billing">Subscription & Billing</SelectItem>
                    <SelectItem value="Technical Support">Technical Support</SelectItem>
                    <SelectItem value="Schools & Partnerships">Schools & Partnerships</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-foreground font-medium text-sm">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell us how we can help..."
                  className="min-h-[140px] rounded-xl border-border focus:border-primary focus:ring-primary"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="bg-primary hover:bg-[#066d9c] text-white rounded-xl px-10 font-semibold"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>

          {/* Right: Contact Info */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Other Ways to Reach Us</h2>
            <p className="text-muted-foreground text-sm mb-8">Choose the option that works best for you.</p>

            <div className="space-y-6">
              {/* Email */}
              <div className="bg-muted/40 rounded-2xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-foreground mb-1">Email</p>
                    <a href="mailto:hello@edufocalgroup.com" className="text-primary hover:text-[#066d9c] font-medium transition-colors">
                      hello@edufocalgroup.com
                    </a>
                    <p className="text-muted-foreground text-sm mt-1">We respond within 24 hours</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-muted/40 rounded-2xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-foreground mb-1">Office</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      The Summit Suite 2014<br />
                      16 Chelsea Avenue<br />
                      Kingston 5, Jamaica, W.I.
                    </p>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-muted/40 rounded-2xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-400 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-foreground mb-1">Hours</p>
                    <p className="text-muted-foreground text-sm">Monday - Friday, 9:00 AM - 5:00 PM (EST)</p>
                    <p className="text-muted-foreground text-xs mt-1">Messages outside business hours will be responded to the next business day</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}
